import React, { useState, useRef } from "react";
import emailjs from "emailjs-com";
import { MessageSquare, Phone, MapPin, Mail } from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ContactForm = () => {
  const form = useRef();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
    phone: "", // Added phone field
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    // Phone number validation (10 digits)
    const phoneRegex = /^[0-9]{10}$/;
    if (!phoneRegex.test(formData.phone)) {
      setLoading(false);
      alert("Phone number must be exactly 10 digits.");
      return;
    }

    emailjs
      .sendForm("service_6ztxc8g", "template_gqh7qxu", form.current, "yVNXoiryRF-4YH2Mi")
      .then(
        (result) => {
          console.log("Email sent successfully:", result.text);
          setLoading(false);
          toast.success('Message sent successfully!');
          
          setFormData({ name: "", email: "", subject: "", message: "", phone: "" }); // Reset phone field too
        },
        (error) => {
          console.log("Email sending failed:", error.text);
          setLoading(false);
          alert("Failed to send message. Please try again later.");
        }
      );
  };

  return (
    <>  
      <ToastContainer/>   
      <div className="w-full bg-white">
        {/* Header with Image */}
        <div className="relative h-48 bg-white flex items-center justify-center">
          <img
            src="https://images.pexels.com/photos/821754/pexels-photo-821754.jpeg?auto=compress&cs=tinysrgb&w=2070"
            alt="Vegetables header"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <h1 className="text-4xl font-bold text-black relative z-10">Contact Us</h1>
        </div>

        {/* Contact Info Cards */}
        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[{
              icon: <Mail className="w-8 h-8 text-white" />,
              title: "Email Us",
              info: "info@ecomify.com"
            }, {
              icon: <Phone className="w-8 h-8 text-white" />,
              title: "Call Us",
              info: "+91 8056016197"
            }, {
              icon: <MapPin className="w-8 h-8 text-white" />,
              title: "Location",
              info: "Boho One, Bridge Street West, Middlesbrough, UK"
            }].map((item, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-md text-center">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center">
                    {item.icon}
                  </div>
                </div>
                <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                <p className="text-emerald-500 mb-2">{item.info}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Contact Form Section */}
        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="flex flex-col md:flex-row gap-12">
            {/* Image */}
            <div className="md:w-1/2">
              <img
                src="https://kachabazar-store-nine.vercel.app/_next/image?url=%2Fcontact-us.png&w=1080&q=75"
                alt="Contact illustration"
                className="w-full h-auto"
              />
            </div>

            {/* Form */}
            <div className="md:w-1/2">
              <div className="mb-8">
                <h2 className="text-2xl font-bold mb-2">Send Your Query</h2>
                <p className="text-gray-600">
                  Have a question? Send us a message and we'll get back to you shortly.
                </p>
              </div>

              <form ref={form} onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your Name"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-emerald-500"
                  />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Your Email"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-emerald-500"
                  />
                </div>

                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Your Phone (10 digits)"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-emerald-500"
                  pattern="[0-9]{10}"
                  title="Phone number must be exactly 10 digits"
                />

                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="Subject"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-emerald-500"
                />

                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Write your message here"
                  rows="6"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-emerald-500 resize-none"
                />

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full md:w-auto px-8 py-3 bg-emerald-500 text-white rounded-md hover:bg-emerald-600 transition-all"
                >
                  {loading ? "Sending..." : "Send Message"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ContactForm;
